import { urlHasCoachesOnlineShortcode, urlHasMesoShortcode } from 'utils/handoffShortcodes';

import {
  Bonus,
  BonusSlots,
  BonusesContent,
  DEFAULT_BUTTON_TEXT,
  DEFAULT_TITLE,
  HANDOFF_PARTNERS,
  HandoffPartner,
} from './bonuses';

/**
 * The bonuses popup and Home banner are authored as "Bonus card" blocks in each course's
 * bonus-section-upgrade manual (admin: services/editor/tools/bonusCard). The block type and field
 * names must match that admin tool.
 */
export const BONUS_CARD_BLOCK_TYPE = 'bonusCard';

export type BonusCardColumn = 'partners' | 'ours';

export type BonusCardBlockData = {
  bonusId: string;
  column: BonusCardColumn;
  /** Position among the Home banner tiles (1 = first). Empty leaves the card off the banner. */
  bannerOrder?: number;
  name: string;
  shortName?: string;
  logo?: string;
  logoDark?: string;
  logoAlt?: string;
  invertLogoInDark?: boolean;
  isTallLogo?: boolean;
  blurb: string;
  value: string;
  shortValue?: string;
  detail: string;
  memberDetail?: string;
  maxUnits?: number;
  delayHours?: number;
  cooldownHours?: number;
  finalLowUnitsThreshold?: number;
  website: string;
  /**
   * What Unlock opens for MVP purchasers. A partner activation shortcode — `https://[meso-core]/` or
   * `http://[coachesonline-mvp]/` — becomes that partner's freshly minted sign-in link.
   */
  unlockUrl?: string;
  /** Older cards chose the sign-in partner here instead of using a shortcode; still honoured. */
  unlockType?: 'link' | HandoffPartner;
  openLabel?: string;
  code?: string;
  codeNote?: string;
};

type ManualLike = { title?: unknown; buttonText?: unknown; content?: unknown } | null | undefined;

const isHandoffPartner = (value: unknown): value is HandoffPartner =>
  typeof value === 'string' && value in HANDOFF_PARTNERS;

const text = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const number = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(text(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const flag = (value: unknown) => value === true || value === 'true';

/** Drops unset optional fields so a block (or card) holds only what is actually set. */
const compact = <T extends Record<string, unknown>>(record: T): T =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined)) as T;

/** One card as an admin block — used to seed the manuals with exactly the shipped content. */
export const blockDataFromBonus = (bonus: Bonus, column: BonusCardColumn, bannerOrder?: number): BonusCardBlockData => {
  const { unlock, slots } = bonus;

  return compact({
    bonusId: bonus.id,
    column,
    bannerOrder,
    name: bonus.name,
    shortName: bonus.shortName,
    logo: bonus.logo,
    logoDark: bonus.logoDark,
    logoAlt: bonus.logoAlt,
    invertLogoInDark: bonus.invertLogoInDark || undefined,
    isTallLogo: bonus.isTallLogo || undefined,
    blurb: bonus.blurb,
    value: bonus.value,
    shortValue: bonus.shortValue,
    detail: bonus.detail,
    memberDetail: bonus.memberDetail,
    maxUnits: slots?.maxUnits,
    delayHours: slots?.delayHours,
    cooldownHours: slots?.cooldownHours,
    finalLowUnitsThreshold: slots?.finalLowUnitsThreshold,
    website: bonus.website,
    unlockUrl: unlock.kind === 'handoff' ? unlock.shortcodeUrl : unlock.url,
    // A handoff's label is the partner default unless it was changed.
    openLabel:
      unlock.kind === 'link' || unlock.openLabel !== HANDOFF_PARTNERS[unlock.partner].openLabel
        ? unlock.openLabel
        : undefined,
    code: unlock.kind === 'link' ? unlock.code : undefined,
    codeNote: unlock.kind === 'link' ? unlock.codeNote : undefined,
  });
};

const slotsFromBlock = (data: Record<string, unknown>): BonusSlots | undefined => {
  const maxUnits = number(data.maxUnits);
  if (!maxUnits || maxUnits <= 0) return undefined;

  return compact({
    maxUnits,
    delayHours: number(data.delayHours),
    cooldownHours: number(data.cooldownHours),
    finalLowUnitsThreshold: number(data.finalLowUnitsThreshold),
  });
};

/** The sign-in partner a card claims through: an activation shortcode in its unlock URL, or the older dropdown. */
const getHandoffPartner = (unlockType: unknown, unlockUrl?: string): HandoffPartner | undefined => {
  if (isHandoffPartner(unlockType)) return unlockType;
  if (!unlockUrl) return undefined;
  if (urlHasMesoShortcode(unlockUrl)) return 'meso';
  if (urlHasCoachesOnlineShortcode(unlockUrl)) return 'coaches-online';
  return undefined;
};

type ParsedCard = { bonus: Bonus; column: BonusCardColumn; bannerOrder?: number };

/** Reads one block back into a card. Anything without a name is skipped. */
export const bonusFromBlockData = (raw: unknown, index: number): ParsedCard | null => {
  const data = (raw || {}) as Record<string, unknown>;
  const name = text(data.name);
  if (!name) return null;

  const website = text(data.website) ?? '';
  const unlockUrl = text(data.unlockUrl);
  const partner = getHandoffPartner(data.unlockType, unlockUrl);

  const unlock: Bonus['unlock'] = partner
    ? {
        kind: 'handoff',
        partner,
        ...HANDOFF_PARTNERS[partner],
        openLabel: text(data.openLabel) ?? HANDOFF_PARTNERS[partner].openLabel,
      }
    : compact({
        kind: 'link' as const,
        // A link card without its own claim URL falls back to its public page.
        url: unlockUrl ?? website,
        openLabel: text(data.openLabel) ?? `Open ${name}`,
        code: text(data.code),
        codeNote: text(data.codeNote),
      });

  const bonus: Bonus = compact({
    id: text(data.bonusId) ?? `bonus-${index + 1}`,
    name,
    shortName: text(data.shortName),
    logo: text(data.logo),
    logoDark: text(data.logoDark),
    logoAlt: text(data.logoAlt),
    invertLogoInDark: flag(data.invertLogoInDark) || undefined,
    isTallLogo: flag(data.isTallLogo) || undefined,
    blurb: text(data.blurb) ?? '',
    value: text(data.value) ?? '',
    shortValue: text(data.shortValue),
    detail: text(data.detail) ?? '',
    memberDetail: text(data.memberDetail),
    slots: slotsFromBlock(data),
    website,
    unlock,
  });

  const bannerOrder = number(data.bannerOrder);

  return {
    bonus,
    column: data.column === 'ours' ? 'ours' : 'partners',
    bannerOrder: bannerOrder && bannerOrder > 0 ? bannerOrder : undefined,
  };
};

/**
 * The popup and banner content from a bonus-section-upgrade manual, or null when the manual has no
 * Bonus card blocks yet — the caller then keeps the shipped defaults.
 */
export const parseBonusesManual = (manual: ManualLike): BonusesContent | null => {
  const blocks = (manual?.content as { blocks?: unknown } | undefined)?.blocks;
  if (!Array.isArray(blocks)) return null;

  const cards = blocks
    .filter((block) => block?.type === BONUS_CARD_BLOCK_TYPE)
    .map((block, index) => bonusFromBlockData(block.data, index))
    .filter((card): card is ParsedCard => !!card);

  if (!cards.length) return null;

  const ordered = cards.filter((card) => card.bannerOrder !== undefined);
  const bannerTiles = ordered.length
    ? [...ordered].sort((a, b) => (a.bannerOrder as number) - (b.bannerOrder as number)).map((card) => card.bonus)
    : cards.map((card) => card.bonus);

  return {
    title: text(manual?.title) ?? DEFAULT_TITLE,
    buttonText: text(manual?.buttonText) ?? DEFAULT_BUTTON_TEXT,
    partners: cards.filter((card) => card.column === 'partners').map((card) => card.bonus),
    ours: cards.filter((card) => card.column === 'ours').map((card) => card.bonus),
    bannerTiles,
  };
};
